"use client";

import { Button, Input, cn, toast } from "@devhop/ui";
import { CheckIcon, ChevronLeftIcon, SearchIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  useSetWorkshopVehicleConfigMutation,
  useWorkshopVehicleConfigurationQuery,
  useWorkshopVehicleConfigurationsQuery,
  useWorkshopVehicleMakesQuery,
  useWorkshopVehicleModelsQuery,
} from "../api/queries";
import { workshopToastError } from "../lib/workshop-user-messages";

type Step = "make" | "model" | "config";

function filterByQ<T>(items: T[], q: string, getLabel: (item: T) => string): T[] {
  const s = q.trim().toLowerCase();
  if (!s) return items;
  return items.filter((item) => getLabel(item).toLowerCase().includes(s));
}

export function WorkshopVehicleConfigPicker({
  shopId,
  jobId,
  vehicleConfigurationId,
  onConfigurationChange,
  onSaved,
}: {
  shopId: string;
  /** ຖ້າບໍ່ມີ = ເລືອກແບບ draft (ຍັງບໍ່ມີໃບງານ) */
  jobId?: string;
  vehicleConfigurationId: string | null;
  onConfigurationChange?: (vehicleConfigurationId: string | null) => void;
  onSaved?: () => void;
}) {
  const existingQ = useWorkshopVehicleConfigurationQuery(
    shopId,
    vehicleConfigurationId,
  );

  const [step, setStep] = useState<Step>(
    vehicleConfigurationId ? "config" : "make",
  );
  const [makeId, setMakeId] = useState("");
  const [makeName, setMakeName] = useState("");
  const [modelId, setModelId] = useState("");
  const [modelName, setModelName] = useState("");
  const [configId, setConfigId] = useState(vehicleConfigurationId ?? "");
  const [q, setQ] = useState("");
  const [hydrated, setHydrated] = useState(false);

  const setConfigMut = useSetWorkshopVehicleConfigMutation(shopId);
  const makesQ = useWorkshopVehicleMakesQuery(shopId);
  const modelsQ = useWorkshopVehicleModelsQuery(
    shopId,
    makeId || undefined,
  );
  const configsQ = useWorkshopVehicleConfigurationsQuery(
    shopId,
    modelId || undefined,
  );

  useEffect(() => {
    if (hydrated || !existingQ.data) return;
    setMakeId(existingQ.data.makeId);
    setMakeName(existingQ.data.makeName);
    setModelId(existingQ.data.modelId);
    setModelName(existingQ.data.modelName);
    setConfigId(existingQ.data.id);
    setStep("config");
    setHydrated(true);
  }, [existingQ.data, hydrated]);

  useEffect(() => {
    setConfigId(vehicleConfigurationId ?? "");
    if (!vehicleConfigurationId) {
      setHydrated(false);
    }
  }, [vehicleConfigurationId]);

  const makes = useMemo(
    () =>
      filterByQ(makesQ.data?.items ?? [], q, (m) => m.name),
    [makesQ.data?.items, q],
  );
  const models = useMemo(
    () =>
      filterByQ(modelsQ.data?.items ?? [], q, (m) => m.name),
    [modelsQ.data?.items, q],
  );
  const configs = useMemo(
    () =>
      filterByQ(
        configsQ.data?.items ?? [],
        q,
        (c) => c.label || c.modelCode || c.id,
      ),
    [configsQ.data?.items, q],
  );

  const displayName =
    existingQ.data?.displayName ||
    [makeName, modelName].filter(Boolean).join(" · ") ||
    null;

  async function pickConfig(id: string, label: string) {
    setConfigId(id);
    if (!jobId) {
      onConfigurationChange?.(id);
      toast.success(`ເລືອກ: ${label}`);
      onSaved?.();
      return;
    }
    try {
      await setConfigMut.mutateAsync({
        jobId,
        vehicleConfigurationId: id,
      });
      toast.success(`ບັນທຶກ: ${label}`);
      onSaved?.();
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function clearConfig() {
    setMakeId("");
    setMakeName("");
    setModelId("");
    setModelName("");
    setConfigId("");
    setStep("make");
    setQ("");
    setHydrated(false);
    if (!jobId) {
      onConfigurationChange?.(null);
      toast.success("ລຶບລຸ້ນລົດແລ້ວ");
      onSaved?.();
      return;
    }
    try {
      await setConfigMut.mutateAsync({
        jobId,
        vehicleConfigurationId: null,
      });
      toast.success("ລຶບລຸ້ນລົດແລ້ວ");
      onSaved?.();
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  function goMake() {
    setMakeId("");
    setMakeName("");
    setModelId("");
    setModelName("");
    setConfigId("");
    setStep("make");
    setQ("");
  }

  function goModel() {
    setModelId("");
    setModelName("");
    setConfigId("");
    setStep("model");
    setQ("");
  }

  const stepTitle =
    step === "make"
      ? "1/3 ເລືອກຍີ່ຫໍ້"
      : step === "model"
        ? "2/3 ເລືອກລຸ້ນ"
        : "3/3 ເລືອກປີ / ເຄື່ອງ";

  const loading =
    (step === "make" && makesQ.isLoading) ||
    (step === "model" && modelsQ.isLoading) ||
    (step === "config" && configsQ.isLoading);

  const list =
    step === "make" ? makes : step === "model" ? models : configs;

  return (
    <div className="space-y-3">
      {displayName && vehicleConfigurationId ? (
        <div className="flex items-start justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 text-sm">
          <div className="min-w-0">
            <p className="text-emerald-800 text-xs">ລຸ້ນລົດປັດຈຸບັນ</p>
            <p className="font-medium text-emerald-950 leading-snug">
              {displayName}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="shrink-0 text-emerald-900"
            disabled={setConfigMut.isPending}
            onClick={() => void clearConfig()}
          >
            <XIcon className="size-4" />
            ລຶບ
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {makeName ? (
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-full border border-border bg-background px-3 text-xs font-medium"
            onClick={goMake}
          >
            {makeName}
            <XIcon className="size-3 text-muted-foreground" />
          </button>
        ) : null}
        {modelName ? (
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-full border border-border bg-background px-3 text-xs font-medium"
            onClick={goModel}
          >
            {modelName}
            <XIcon className="size-3 text-muted-foreground" />
          </button>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-sm">{stepTitle}</p>
        {step !== "make" ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
            onClick={() => {
              if (step === "config") goModel();
              else goMake();
            }}
          >
            <ChevronLeftIcon className="size-3.5" />
            ກັບ
          </button>
        ) : null}
      </div>

      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-11 pl-9"
          placeholder={
            step === "make"
              ? "ຄົ້ນຍີ່ຫໍ້ ເຊັ່ນ Toyota…"
              : step === "model"
                ? "ຄົ້ນລຸ້ນ ເຊັ່ນ Hilux…"
                : "ຄົ້ນປີ / ເຄື່ອງ…"
          }
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
      </div>

      {loading ? (
        <p className="py-6 text-center text-muted-foreground text-xs">
          ກຳລັງໂຫຼດ…
        </p>
      ) : list.length === 0 ? (
        <p className="py-6 text-center text-muted-foreground text-xs">
          ບໍ່ພົບລາຍການ
        </p>
      ) : (
        <ul className="max-h-72 space-y-1.5 overflow-y-auto overscroll-contain">
          {step === "make"
            ? makes.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={cn(
                      "flex h-12 w-full items-center justify-between rounded-xl border px-3 text-left text-sm font-medium transition",
                      makeId === m.id
                        ? "border-primary bg-primary/10"
                        : "border-border bg-background hover:border-primary/40",
                    )}
                    onClick={() => {
                      setMakeId(m.id);
                      setMakeName(m.name);
                      setModelId("");
                      setModelName("");
                      setConfigId("");
                      setStep("model");
                      setQ("");
                    }}
                  >
                    {m.name}
                    {makeId === m.id ? (
                      <CheckIcon className="size-4 text-primary" />
                    ) : null}
                  </button>
                </li>
              ))
            : null}

          {step === "model"
            ? models.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={cn(
                      "flex h-12 w-full items-center justify-between rounded-xl border px-3 text-left text-sm font-medium transition",
                      modelId === m.id
                        ? "border-primary bg-primary/10"
                        : "border-border bg-background hover:border-primary/40",
                    )}
                    onClick={() => {
                      setModelId(m.id);
                      setModelName(m.name);
                      setConfigId("");
                      setStep("config");
                      setQ("");
                    }}
                  >
                    {m.name}
                    {modelId === m.id ? (
                      <CheckIcon className="size-4 text-primary" />
                    ) : null}
                  </button>
                </li>
              ))
            : null}

          {step === "config"
            ? configs.map((c) => {
                const label = c.label || c.modelCode || c.id.slice(0, 8);
                const selected = configId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={jobId ? setConfigMut.isPending : false}
                      className={cn(
                        "flex min-h-12 w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition",
                        selected
                          ? "border-primary bg-primary/10 font-medium"
                          : "border-border bg-background hover:border-primary/40",
                      )}
                      onClick={() => void pickConfig(c.id, label)}
                    >
                      <span className="leading-snug">{label}</span>
                      {selected ? (
                        <CheckIcon className="size-4 shrink-0 text-primary" />
                      ) : null}
                    </button>
                  </li>
                );
              })
            : null}
        </ul>
      )}
    </div>
  );
}
